import {
  createContext,
  ReactElement,
  SyntheticEvent,
  useContext,
  useReducer,
  useState,
} from "react";
import LoadingBackDrop from "../Reuseables/LoadingBackDrop";
import { Alert, PaletteMode, Snackbar, SnackbarCloseReason } from "@mui/material";
import { Severity } from "../../types/enums";

interface ApplicationContextType {
  isLoading: boolean;
  isSnackbarOpen: boolean;
  message?: string;
  snackbarMessage?: string;
  severity: any;
  showLoading: (text: string) => void;
  stopLoading: () => void;
  showSnackbar: (text: string, severity: Severity) => void;
  closeSnackBar: () => void;

}

const initialState = {
  isLoading: false,
  isSnackbarOpen: false,
  message: "",
  snackbarMessage: "",
  severity: undefined,
  showLoading: () => {},
  stopLoading: () => {},
  showSnackbar: () => {},
  closeSnackBar: () => {},
 
};

const ApplicationContext = createContext<ApplicationContextType>(initialState);

type Payload = {
  message?: string;
  snackbarMessage?: string;
  severity: any;
};

type ApplicationAction =
  | { type: "SHOW_SNACKBAR"; payload: Payload }
  | { type: "CLOSE_SNACKBAR"; payload: Payload }
  | { type: "SHOW_LOADING"; payload: Payload }
  | { type: "STOP_LOADING"; payload: Payload };

const reducer = (state: ApplicationContextType, action: ApplicationAction) => {
  switch (action.type) {
    case "SHOW_SNACKBAR":
      return {
        ...state,
        isSnackbarOpen: true,
        snackbarMessage: action.payload.snackbarMessage,
        severity: action.payload.severity,
      };
    case "CLOSE_SNACKBAR":
      return {
        ...state,
        isSnackbarOpen: false,
      };
    case "SHOW_LOADING":
      return {
        ...state,
        isLoading: true,
        message: action.payload.message,
      };
    case "STOP_LOADING":
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
};

const ApplicationProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  
  const closeSnackbar = (
    _: Event | SyntheticEvent<any, Event>,
    reason: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch({
      type: "CLOSE_SNACKBAR",
      payload: { message: "", severity: "success" },
    });
  };

  const showSnackbar = (snackbarMessage: string, severity: Severity) => {
    dispatch({
      type: "SHOW_SNACKBAR",
      payload: { snackbarMessage, severity },
    });
  };

  const showLoading = (message: string) => {
    dispatch({
      type: "SHOW_LOADING",
      payload: { message, severity: "success" },
    });
  };

  const stopLoading = () => {
    dispatch({
      type: "STOP_LOADING",
      payload: { message: "", severity: "success" },
    });
  };


  return (
    <ApplicationContext.Provider
      value={{ ...state, showSnackbar, showLoading, stopLoading}}
    >
      {children}
      <LoadingBackDrop
        open={state.isLoading}
        message={state.message ? state.message : ""}
      />
      <Snackbar
        open={state.isSnackbarOpen}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        {/* @ts-ignore */}
        <Alert onClose={closeSnackbar} severity={state.severity}>
          {state.snackbarMessage}
        </Alert>
      </Snackbar>
    </ApplicationContext.Provider>
  );
};

function useApplicationContext() {
  return useContext(ApplicationContext);
}

export { useApplicationContext, ApplicationProvider };
